/**
 * Resilience Reporter — 예상된 churn 과 진짜 장애의 구조적 분리 레이어
 *
 * ISSUE_155: 탭 복귀/네트워크 이벤트 직후 발생하는 WS 재구독/auth refresh timeout
 * 같은 "예상된 churn" 을 콘솔/Sentry error 로 쏟아내지 않고, 복원 윈도우 안에서
 * 버퍼링한 뒤 요약 1줄로 보고한다. 윈도우 타임아웃 혹은 진짜 미회복분만 error 승격.
 *
 * 책임:
 * - 윈도우 open/close 상태머신 (idempotent)
 * - 이벤트 버퍼 + 중복 집계 (같은 kind+detail 은 count 만 증가)
 * - 요약 로그 생성 + Sentry breadcrumb
 * - 자동 close 판정 (모든 expected 채널 복귀 + auth 복귀)
 * - 윈도우 밖 report 는 reportImmediate 로 위임
 *
 * 비책임:
 * - 복구 로직 자체 — ResilienceCoordinator 담당
 * - 복구 이벤트 디스패치 — ResilienceCoordinator 가 트리거
 * - 채널 재구독 — RealtimeManager 담당
 *
 * @module core/resilience/ResilienceReporter
 */

import { Sentry } from '~/shared/lib/sentry';

// =============================================================================
// TYPES
// =============================================================================

export type WindowReason =
  | 'tab_resume_short'     // 5~60초 hidden 복귀
  | 'tab_resume_long'      // 60초+ hidden 복귀
  | 'online_event'         // navigator online 이벤트
  | 'auth_refresh_timeout' // AuthBroker 1회 timeout 트리거
  | 'manual';

export type EventKind =
  | 'channel_error'
  | 'channel_timeout'
  | 'auth_refresh_transient'
  | 'auth_refresh_null';

export interface ResilienceEvent {
  kind: EventKind;
  detail: string;
  timestamp?: number;
}

// =============================================================================
// CONSTANTS
// =============================================================================

/** 윈도우 타임아웃 — 이 시간 내에 auto-close 안되면 partial 판정 */
const WINDOW_TIMEOUT_MS = 10 * 1000;

// =============================================================================
// INTERNAL STATE
// =============================================================================

interface BufferedEntry {
  event: ResilienceEvent;
  count: number;
  firstSeen: number;
  lastSeen: number;
}

interface OpenWindowState {
  reason: WindowReason;
  openedAt: number;
  timeoutTimer: ReturnType<typeof setTimeout>;
  /**
   * 실제로 channel_error 가 발생한 채널 집합 (lazy).
   * "탭 복귀 시 모든 채널이 재구독되어야 한다" 는 잘못된 전제 —
   * 이미 SUBSCRIBED 인 채널은 이벤트 재발화 없음. 에러 본 채널만 추적.
   */
  trackedChannels: Set<string>;
  recoveredChannels: Set<string>;
  authTransientSeen: boolean;
  authRecovered: boolean;
  buffer: Map<string, BufferedEntry>;
}

// =============================================================================
// REPORTER
// =============================================================================

class ResilienceReporter {
  private windowState: OpenWindowState | null = null;

  /**
   * 복원 윈도우 열기.
   * 이미 열려있으면 idempotent — reason 유지, no-op.
   * 콘솔 로그 없음 — close 시점에 실제 이벤트 있었던 경우에만 요약 로그 출력.
   */
  openWindow(reason: WindowReason): void {
    if (typeof window === 'undefined') return; // SSR guard

    if (this.windowState) {
      return;
    }

    this.windowState = {
      reason,
      openedAt: Date.now(),
      timeoutTimer: setTimeout(() => this.handleTimeout(), WINDOW_TIMEOUT_MS),
      trackedChannels: new Set(),
      recoveredChannels: new Set(),
      authTransientSeen: false,
      authRecovered: false,
      buffer: new Map(),
    };

    Sentry.addBreadcrumb({
      category: 'resilience',
      message: `Window opened: ${reason}`,
      level: 'info',
    });
  }

  /**
   * 윈도우 닫기 — 성공/부분 자동 판정.
   * - Clean (이벤트 0): 콘솔 로그 생략, breadcrumb 만
   * - 성공: trackedChannels 모두 복귀 + auth OK → info 1줄
   * - 부분: 미회복분 error 승격
   */
  closeWindow(): void {
    if (!this.windowState) return;

    const w = this.windowState;
    clearTimeout(w.timeoutTimer);
    this.windowState = null;

    const elapsedMs = Date.now() - w.openedAt;
    const elapsedSec = (elapsedMs / 1000).toFixed(1);
    const trackedCount = w.trackedChannels.size;
    const recoveredCount = Array.from(w.trackedChannels).filter((c) =>
      w.recoveredChannels.has(c),
    ).length;
    const missingChannels = Array.from(w.trackedChannels).filter(
      (c) => !w.recoveredChannels.has(c),
    );
    const authOk = !w.authTransientSeen || w.authRecovered;
    const hasEvents = trackedCount > 0 || w.authTransientSeen;
    const isSuccess = missingChannels.length === 0 && authOk;

    // Clean window — 아무 churn 없이 윈도우 타임아웃. 조용히 닫기.
    if (!hasEvents) {
      Sentry.addBreadcrumb({
        category: 'resilience',
        message: 'Window closed (clean)',
        data: { reason: w.reason, elapsedMs },
        level: 'debug',
      });
      return;
    }

    if (isSuccess) {
      const parts: string[] = [];
      if (trackedCount > 0) parts.push(`${recoveredCount} channel events resolved`);
      if (w.authTransientSeen) parts.push('auth OK');
      console.info(`[Resilience] ${w.reason} recovered — ${parts.join(', ')} in ${elapsedSec}s`);

      Sentry.addBreadcrumb({
        category: 'resilience',
        message: 'Window closed (success)',
        data: {
          reason: w.reason,
          trackedCount,
          recoveredCount,
          authTransient: w.authTransientSeen,
          elapsedMs,
        },
        level: 'info',
      });
    } else {
      console.warn(
        `[Resilience] ${w.reason} partial — ${recoveredCount}/${trackedCount} channels OK in ${elapsedSec}s`,
      );
      for (const ch of missingChannels) {
        console.error(`[Resilience] Channel failed to recover: ${ch}`);
      }
      if (w.authTransientSeen && !w.authRecovered) {
        console.error('[Resilience] Auth refresh did not recover within window');
      }

      Sentry.addBreadcrumb({
        category: 'resilience',
        message: 'Window closed (partial)',
        data: {
          reason: w.reason,
          trackedCount,
          recoveredCount,
          missingChannels,
          authRecovered: w.authRecovered,
          elapsedMs,
        },
        level: 'warning',
      });
    }
  }

  /**
   * 이벤트 신고.
   * 윈도우 중이면 버퍼 + breadcrumb 1회, 아니면 reportImmediate 위임.
   */
  report(event: ResilienceEvent): void {
    if (!this.windowState) {
      this.reportImmediate(event);
      return;
    }

    const w = this.windowState;
    const ts = event.timestamp ?? Date.now();
    const key = `${event.kind}:${event.detail}`;
    const existing = w.buffer.get(key);

    if (existing) {
      existing.count += 1;
      existing.lastSeen = ts;
    } else {
      w.buffer.set(key, { event, count: 1, firstSeen: ts, lastSeen: ts });
      Sentry.addBreadcrumb({
        category: 'resilience',
        message: `${event.kind}: ${event.detail}`,
        level: 'info',
      });
    }

    // tracked 에 추가 (채널 에러/타임아웃만)
    if (event.kind === 'channel_error' || event.kind === 'channel_timeout') {
      w.trackedChannels.add(event.detail);
    }

    if (event.kind === 'auth_refresh_transient' || event.kind === 'auth_refresh_null') {
      w.authTransientSeen = true;
    }
  }

  /**
   * 즉시 error 승격 (윈도우 무관). 진짜 장애 신호.
   */
  reportImmediate(event: ResilienceEvent): void {
    const msg = `[Resilience] Unexpected ${event.kind}: ${event.detail}`;
    console.error(msg);

    Sentry.addBreadcrumb({
      category: 'resilience',
      message: msg,
      level: 'error',
    });
  }

  /**
   * 채널 복귀 마킹 — tracked 에 있던 채널일 때만 의미 있음.
   * auto-close 판정 트리거.
   */
  markChannelRecovered(name: string): void {
    if (!this.windowState) return;
    if (this.windowState.trackedChannels.has(name)) {
      this.windowState.recoveredChannels.add(name);
      this.tryAutoClose();
    }
  }

  /**
   * Auth 복귀 마킹 — auto-close 판정 트리거.
   */
  markAuthRecovered(): void {
    if (!this.windowState) return;
    this.windowState.authRecovered = true;
    this.tryAutoClose();
  }

  isWindowOpen(): boolean {
    return this.windowState !== null;
  }

  getWindowReason(): WindowReason | null {
    return this.windowState?.reason ?? null;
  }

  /** 테스트 전용 — 내부 상태 초기화 */
  _reset(): void {
    if (this.windowState?.timeoutTimer) {
      clearTimeout(this.windowState.timeoutTimer);
    }
    this.windowState = null;
  }

  // ===========================================================================
  // PRIVATE
  // ===========================================================================

  private tryAutoClose(): void {
    if (!this.windowState) return;
    const w = this.windowState;

    // 아무 이벤트도 없으면 auto-close 하지 않음 (타임아웃까지 대기).
    // 탭 복귀 직후 짧은 시간 내 발생할 수 있는 churn 을 기다리기 위함.
    const hasEvents = w.trackedChannels.size > 0 || w.authTransientSeen;
    if (!hasEvents) return;

    const allChannelsRecovered = Array.from(w.trackedChannels).every((c) =>
      w.recoveredChannels.has(c),
    );
    const authOk = !w.authTransientSeen || w.authRecovered;

    if (allChannelsRecovered && authOk) {
      this.closeWindow();
    }
  }

  private handleTimeout(): void {
    this.closeWindow();
  }
}

// =============================================================================
// SINGLETON EXPORT
// =============================================================================

export const resilienceReporter = new ResilienceReporter();
